import { Box, Checkbox, CircularProgress, Divider, FormHelperText, Grid, IconButton, InputAdornment, lighten, Link, List, ListItem, ListItemIcon, 
  ListItemText, ListSubheader, Popover, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { FC, Fragment, MutableRefObject, useRef, useMemo, useReducer, useState, useEffect, useContext } from "react";
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { VariableSizeList } from 'react-window';
import { FormikHelpers, useFormikContext } from "formik";
import { ObfuscationDataType, ObfuscationRuleDto } from "@/gen/api/encrypt";
import { ReduxState, useRecordContext, useSaveContext } from "ra-core";
import { RecPatch } from "@/dataProvider/tools";
import { useDispatch, useSelector } from "react-redux";
import { displayInferredType, inferDataTypeInfo, Inferred } from "./obfsRuleCompatibleMapping";
import { validators } from "./obfsRuleValidators";
import { DropEvent, useDropzone } from "react-dropzone";
import Create from "@/controller/Create";
import { useSimpleSaveAction } from "@/controller/SaveContextProvider";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import { ObfuscationRuleListContext } from "@/pages/settings/encryption/ObfuscationRule/ObfuscationRuleList";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { MANAGE_OBFUSCATION_RULES } from "@/constants";
import { crudGetOneEx } from "@/actions/crudFetchEx";


interface Values {
  dataType: ObfuscationDataType,
  valuesList: string[],
  inferredType: Inferred|null,
}

interface States {
  valuesList: string[],
  delValues: Record<string, true>,
  addValues: string[],
  updateCount: number,
}
type Action =
| { type: 'reset',  payload?: string[] }
| { type: 'add',    payload: string }
| { type: 'remove', payload: string }

const buildReducer = (
  setValues: FormikHelpers<Values>['setValues'], 
  fieldErrRef: MutableRefObject<{
    errors:Record<string, boolean>, 
    validator: (x:string) => boolean
  }>
) => (prevStates: States, action: Action): States => {
  switch(action.type) {
    case 'reset': {
      const arr = action.payload || []
      setTimeout(() => setValues(prevVals => ({
        ...prevVals,
        valuesList: arr,
        inferredType: inferDataTypeInfo(prevVals.dataType, arr) || null,
      }), false))
      return { valuesList: arr, delValues: {}, addValues: [], updateCount: 0 }
    }
    case 'add': {
      if (prevStates.delValues[action.payload]) {
        if (!fieldErrRef.current.validator(action.payload)) {
          return prevStates
        }
        delete fieldErrRef.current.errors[action.payload]

        const newDelValues = { ...prevStates.delValues }
        delete newDelValues[action.payload]
        const reflectedList = ([] as string[]).concat(prevStates.addValues, prevStates.valuesList).filter(s => !newDelValues[s])
        setTimeout(() => setValues(prevVals => ({
          ...prevVals,
          valuesList: reflectedList,
          inferredType: inferDataTypeInfo(prevVals.dataType, reflectedList) || null,
        })))
        return { 
          ...prevStates, 
          delValues: newDelValues, 
          updateCount: prevStates.updateCount + 1,
        }
      } else if (prevStates.valuesList.indexOf(action.payload) < 0
              && prevStates.addValues.indexOf(action.payload) < 0
      ) {
        const reflectedList = ([] as string[]).concat([action.payload], prevStates.addValues, prevStates.valuesList).filter(s => !prevStates.delValues[s])
        setTimeout(() => setValues(prevVals => ({
          ...prevVals,
          valuesList: reflectedList,
          inferredType: inferDataTypeInfo(prevVals.dataType, reflectedList) || null,
        })))
        return { 
          ...prevStates, 
          addValues: [action.payload, ...prevStates.addValues], 
          updateCount: prevStates.updateCount + 1,
        }
      }
      return prevStates
    }
    case 'remove': {
      const i = prevStates.addValues.indexOf(action.payload)
      if (i > -1) {
        delete fieldErrRef.current.errors[action.payload]

        const newAddValues = [...prevStates.addValues]
        newAddValues.splice(i, 1)
        const reflectedList = ([] as string[]).concat(newAddValues, prevStates.valuesList).filter(s => !prevStates.delValues[s])
        setTimeout(() => setValues(prevVals => ({
          ...prevVals,
          valuesList: reflectedList,
          inferredType: (Object.keys(fieldErrRef.current.errors).length === 0 && 
            inferDataTypeInfo(prevVals.dataType, reflectedList)) 
            || null,
        })))
        return {
          ...prevStates,
          addValues: newAddValues,
          updateCount: prevStates.updateCount - 1,
        }
      } else if (prevStates.valuesList.indexOf(action.payload) > -1) {
        delete fieldErrRef.current.errors[action.payload]

        const newDelValues = { ...prevStates.delValues, [action.payload]: true as const }
        const reflectedList = ([] as string[]).concat(prevStates.addValues, prevStates.valuesList).filter(s => !newDelValues[s])
        setTimeout(() => setValues(prevVals => ({
          ...prevVals,
          valuesList: reflectedList,
          inferredType: (Object.keys(fieldErrRef.current.errors).length === 0 && 
            inferDataTypeInfo(prevVals.dataType, reflectedList)) 
            || null,
        })))
        return {
          ...prevStates,
          delValues: newDelValues,
          updateCount: prevStates.updateCount - 1,
        }
      }
      return prevStates
    }
  }
}


const emptyValuesList: string[] = []
const resource = 'settings/encryption/obfuscation-rule'

const ValuesListRuleInputs: FC = () => {
  const hasWritePermit = useHasPermit([MANAGE_OBFUSCATION_RULES])
  const record = useRecordContext<ObfuscationRuleDto&RecPatch>()
  const mode = !record ? 'create' : 'edit'
  const { values, initialValues, touched, errors, setFieldValue, setValues, validateField, registerField, unregisterField } = useFormikContext<Values>()

  const listRef = useRef<HTMLUListElement|null>(null)
  const newValInputRef = useRef<HTMLInputElement|null>(null)
  const fieldErrRef = useRef({
    errors: {} as Record<string, boolean>,
    validator: validators[values.dataType]
  })
  fieldErrRef.current.validator = validators[values.dataType]

  const reducer = useMemo(() => buildReducer(setValues, fieldErrRef), [setValues])
  const [states, dispatch] = useReducer(reducer, {
    valuesList: initialValues.valuesList || emptyValuesList,
    addValues: [],
    delValues: {},
    updateCount: 0,
  })

  const [innerValues, setInnerValues] = useState({
    valueTypedIn: '',
    valueInputErr: '',
    examplePopAnchor: null as any,
  })
  
  const obfsRuleValuesList = useSelector<ReduxState, string[]>(store => {
    if (record?.valuesList && record.valuesList.length > 0) { return record.valuesList }
    const obfsRule : ObfuscationRuleDto&RecPatch | undefined = 
      record  
      ? store.admin.resources[resource]?.data[record.id]
      : undefined
    return obfsRule?.valuesList || emptyValuesList
  })

  useEffect(() => {
    dispatch({ type: 'reset', payload: obfsRuleValuesList || emptyValuesList })
  }, [obfsRuleValuesList])

  const effectDispatch = useDispatch()
  useEffect(() => {
    if (mode === 'edit') {
      effectDispatch(crudGetOneEx(resource, record.id, ''))
    }
  }, [effectDispatch, record?.id, mode])

  useEffect(() => {
    registerField('valuesList', {
      validate: () => {
        if (Object.keys(fieldErrRef.current.errors).length > 0) {
          return 'There are invalid values in the list'
        }
      }
    })
    return () => {
      unregisterField('valuesList')
    }
  }, [registerField, unregisterField])

  useEffect(() => {
    const reflectedList = ([] as string[]).concat(states.addValues, states.valuesList).filter(s => !states.delValues[s])
    fieldErrRef.current.errors = 
      reflectedList.reduce((acc, s) => {
        if (!fieldErrRef.current.validator(s)) {
          acc[s] = true
        }
        return acc
      }, {} as Record<string, boolean>);
    if (Object.keys(fieldErrRef.current.errors).length === 0) {
      setFieldValue('inferredType',
        inferDataTypeInfo(values.dataType, reflectedList) || null)
    }
    validateField('valuesList')
    setInnerValues(v => ({...v,
      valueTypedIn: '',
      valueInputErr: '',
    }))
  }, [values.dataType, validateField])


  const acceptInput = () => {
    if (!innerValues.valueInputErr) {
      dispatch({ type: 'add', payload: innerValues.valueTypedIn })
      setInnerValues(v => ({...v, valueTypedIn: ''}))
    }
  }
  const handleEnter: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.code === 'Enter' || e.code === 'NumpadEnter') {
      acceptInput()
      e.preventDefault()
    }
  }

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const validate = validators[values.dataType]
    if (!validate(value)) {
      setInnerValues(v => ({...v, valueInputErr: 'Invalid value'}))
    } else {
      setInnerValues(v => ({...v, valueInputErr: ''}))
    }
    setInnerValues(v => ({...v, valueTypedIn: value}))
  }

  const handleRemoveNewlyAdded = (s:string) => {
    const i = states.addValues.indexOf(s)
    if (i === -1) { return }
    if (listRef.current) {
      const btns: HTMLButtonElement[] = [...listRef.current.querySelectorAll('button.MuiIconButton-root')] as any
      const nextFocusBtn = btns[i + 1] || btns[i - 1]
      if (nextFocusBtn) {
        nextFocusBtn.focus()
      } else {
        const cbx = listRef.current.querySelector('input[type=checkbox]') as HTMLInputElement
        if (cbx) { cbx.focus() }
        else { newValInputRef.current && newValInputRef.current.focus() }
      }
    }
    dispatch({ type: 'remove', payload: s })
  }

  const divRef = useRef<HTMLDivElement>({} as any)

  const RenderAdd = ({s, style}:{s: string, style:any}) => {
    return (
      <Fragment key={s}>
        <ListItem style={style} sx={{ borderBottom: theme => `1px solid ${theme.palette.divider}` }}>
          <ListItemIcon>
            <IconButton
              aria-label="remove value"
              onClick={() => handleRemoveNewlyAdded(s)}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </ListItemIcon>
          <ListItemText
            secondary={
              fieldErrRef.current.errors[s] &&
              <Typography variant="caption" color="error.main" >Invalid value</Typography>
            }
          >
            <Typography variant="body1" color="success.main">
              {displayMaybeWithNotation(s)}
            </Typography>
          </ListItemText>
        </ListItem>
      </Fragment>
    )
  }
  const RenderLine = ({s, style}:{s: string, style:any}) => {
    return (
      <Fragment key={s}>
        <ListItem style={style} sx={{ borderBottom: theme => `1px solid ${theme.palette.divider}` }}>
          {hasWritePermit &&
            <ListItemIcon>
              <Checkbox aria-label="remove value" checked={!!states.delValues[s]} onChange={e => dispatch({ type: e.target.checked ? 'remove' : 'add', payload: s })} />
            </ListItemIcon>}
          <ListItemText
            secondary={
              !states.delValues[s] &&
              fieldErrRef.current.errors[s] &&
              <Typography variant="caption" color="error.main" >Invalid value</Typography>
            }
          >
            <Typography 
              variant="body1" 
              color={states.delValues[s] ? 'error.main' : 'default'}
              sx={{ textDecoration: states.delValues[s] ? 'line-through' : undefined }} 
            >
              {displayMaybeWithNotation(s)}
            </Typography>
          </ListItemText>
        </ListItem>
      </Fragment>
    )
  }
  const VRow = ({index, style}: {index:number, style:any}) => {
    return (
      index < states.addValues.length 
      ? <RenderAdd s={states.addValues[index]} style={style} />
      : <RenderLine s={states.valuesList[index - states.addValues.length]} style={style} />
    )
  }
  const [count, setCount] = useState(0)
  useEffect(() => {
    if (!divRef.current || !divRef.current.append) { // continue to check divRef until its available
      setTimeout(() => setCount(v => v + 1), 10)
    } else {
      setTimeout(() => setCount(-1))
    }
  }, [count])

  const getItemSize = (index:number) => {
    if (!divRef.current || !divRef.current.append) { return 50 }
    const s = index < states.addValues.length ? states.addValues[index] : states.valuesList[index - states.addValues.length]
    divRef.current.innerText = ''
    divRef.current.append(String(s))
    return divRef.current.clientHeight + 29
  }
  const listRootRef = useRef<HTMLDivElement>(null as any)
  const listRootWidth = (listRootRef.current?.clientHeight ?? 150 + 48) - 48

  return (
    <>
      <Grid item xs={12} >
        {hasWritePermit &&
          <Stack direction="row" alignItems="center" flexWrap="wrap">
            <TextField 
              inputRef={newValInputRef}
              error={!!innerValues.valueInputErr}
              value={innerValues.valueTypedIn}
              onChange={handleValueChange}
              onKeyDown={handleEnter}
              size="small" 
              placeholder="type new value in here..."
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Add new value">
                      <IconButton
                        arial-label="add new value"
                        onClick={acceptInput}
                      >
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Upload values file">
                      <div><UploadValuesButton /></div>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
              sx={{ flex: '1 0 240px' }}
            />
            <Link sx={{ cursor: 'pointer', pl: 1, mr: -0.75 }}
              onClick={(e:any) => {setInnerValues(v => ({...v, examplePopAnchor: v.examplePopAnchor ? null : e.target}))}}
            >?</Link>
            <Popover
              id="values-file-example-pop"
              open={!!innerValues.examplePopAnchor}
              anchorEl={innerValues.examplePopAnchor}
              onClose={() => {setInnerValues(v => ({...v, examplePopAnchor: null}))}}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              PaperProps={{ sx: { borderRadius: 0.5 } }}
            >
              <Box
                sx={{
                  width: 320,
                  py: 2,
                  '& pre': {
                    mx: 1,
                    p: 1,
                    fontSize: '0.75em',
                    bgcolor: 'divider',
                    '& span.linenumber': {
                      display: 'inline-block',
                      minWidth: '1.25em',
                      paddingRight: '1em',
                      textAlign: 'right',
                      userSelect: 'none',
                      color: 'rgb(98, 114, 164)',
                    },
                  }
                }}
              >
                <Typography sx={{ px: 2 }} variant="caption" component="p">For upload values file: the header "Values" is required, then follows lines of values.</Typography>
                <pre>
                  <code>
                    <span className="linenumber">1</span>
                    <span>Values</span><span>{'\r\n'}</span>
                    <span className="linenumber">2</span>
                    <span>2022-11-17</span><span>{'\r\n'}</span>
                    <span className="linenumber">3</span>
                    <span>2022-10-17</span><span>{'\r\n'}</span>
                    <span className="linenumber">4</span>
                    <span>2022-09-17</span><span>{'\r\n'}</span>
                    <span className="linenumber">5</span>
                    <span>...</span>
                  </code>
                </pre>
              </Box>
            </Popover>
            <Divider sx={{ ml: 2, my: 0.5, width: 16, height: 32, borderBottom: 'none', borderLeftWidth: 1, borderLeftStyle: 'dashed' }} />
            <Typography variant="caption" sx={{ mr: 1 }}>Count: </Typography>
            <Typography variant="subtitle2" color="primary.main" sx={{ minWidth: 24 }}>{states.valuesList.length}</Typography>
            <Typography 
              variant="subtitle2" 
              color={states.updateCount > 0 ? "success.main" : "error.main"}
              sx={{ ml: 0.5, minWidth: 24, opacity: states.updateCount === 0 ? 0 : 1 }}
            >
              {states.updateCount > 0 && '+'}{states.updateCount}
            </Typography>
          </Stack>}
        {innerValues.valueInputErr && <FormHelperText error={true} sx={{ ml: 2 }}>{innerValues.valueInputErr}</FormHelperText>}
        <Box ref={listRootRef} sx={{ height: 'calc(100vh - 588px)' }}>
          <List 
            ref={listRef}
            dense={true} 
            sx={{ 
              mt: 2,
              mx: -2,
              '& .MuiListItem-root': { 
                bgcolor: theme => 
                  theme.palette.mode === 'light'
                  ? lighten(theme.palette.primary.main, 0.98)  
                  : theme.palette.background.default 
              },
              '& > div:last-of-type': {
                overflowX: 'hidden',
              },
            }}
            subheader={
              <ListSubheader>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  Values
                  {displayInferredType(values.inferredType)}
                </Stack>
                {touched.valuesList && errors.valuesList && <FormHelperText error={true}>{errors.valuesList}</FormHelperText>}
              </ListSubheader>
            }
          >
            <Box component="p" ref={divRef} sx={{
              position: 'absolute',
              visibility: 'hidden',
              height: 'auto',
              width: 590,
              fontWeight: 400,
              fontSize: '1rem',
              lineHeight: 1.5,
            }} />
            <Divider />
            {count === -1 &&
              <VariableSizeList
                height={listRootWidth}
                itemCount={states.valuesList.length + states.addValues.length}
                itemSize={getItemSize}
                width={680}
              >
                {VRow}
              </VariableSizeList>}
          </List>
        </Box>
      </Grid>
    </>
  )
}

export default ValuesListRuleInputs;

const rg = /^\s+.*|.*\s+$/

const displayMaybeWithNotation = (x:string) => {
  const isEmpty = !x
  const untrimmed = rg.test(x)
  if (isEmpty || untrimmed) {
    return (
      <>
        <Typography component="span" color="InactiveCaptionText" sx={{ fontStyle: 'italic' }}>"</Typography>
        <Typography component="span" sx={{whiteSpace: 'pre'}}>{x}</Typography>
        <Typography component="span" color="InactiveCaptionText" sx={{ fontStyle: 'italic' }}>"</Typography>
        {isEmpty && <Typography color="InactiveCaptionText" sx={{ ml: 2, fontStyle: 'italic' }} variant="caption">Empty Value</Typography>}
        {untrimmed && <Typography color="InactiveCaptionText" sx={{ ml: 2, fontStyle: 'italic' }} variant="caption">With untrimmed whitespaces</Typography>}
      </>
    )
  } else {
    return x
  }
}

const UploadValuesButton = () => {
  const record = useRecordContext<ObfuscationRuleDto&RecPatch>()
  const handler = useDefaultSavedHandlers("Upload ValueList File")
  return (
    <Create resource="settings-obfs-rule-upload" basePath="" {...handler} record={record}>
      <UploadValuesButtonBody />
    </Create>
  )
}
const UploadValuesButtonBody = () => {
  const record = useRecordContext<ObfuscationRuleDto&RecPatch>()
  const { values } = useFormikContext<Values & {ruleName:string, description:string}>()
  const save = useSimpleSaveAction()
  const { saving } = useSaveContext()
  const { dispatch, refetch } = useContext(ObfuscationRuleListContext)
  const onDropAccepted = (files: File[], event: DropEvent) => {
    const file = files.pop()
    if (!file) { return }
    const payload = {
      ruleName: values.ruleName,
      dataType: values.dataType,
      description: values.description,
      valuesFile: file,
      ruleId: record?.ruleId,
    }
    save(payload, {
      onSuccess: ({data}: {data:any}) => {
        dispatch({ type: 'openEdit', payload: data })
        refetch()
      }
    })
  }
  const { getRootProps, getInputProps } = useDropzone({
    multiple: false,
    maxFiles: 1,
    noDrag: true,
    accept: ['text/*', '.csv', '.txt'],
    onDropAccepted,
  })
  return (
    <IconButton
      arial-label="upload values file"
      {...(getRootProps() as any)}
    >
      <input {...getInputProps()} />
      {saving ? 
        <CircularProgress size="small" sx={{ width: 24 }} /> : 
        <FolderOpenIcon />}
    </IconButton>
  )
}

